import { t } from "@lingui/core/macro"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Save } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { z } from "zod"
import type { Environment } from "@/features/feature-flags/types/flagTypes"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { flagDetailQuery } from "@/features/feature-flags/components/FlagDetail"
import { createUserOverrideFn } from "@/features/feature-flags/server/flagManagement"

const Params = z.object({
  flagId: z.string(),
})

const Search = z.object({
  environment: z.enum(["dev", "staging", "production"]).optional().default("production"),
})

export const Route = createFileRoute(
  "/(authenticated)/settings/feature-flags/$flagId/overrides/new",
)({
  params: Params,
  validateSearch: Search,
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(flagDetailQuery(params.flagId))
  },
  component: NewOverridePage,
})

function NewOverridePage() {
  const { flagId } = Route.useParams()
  const { environment: initialEnv } = Route.useSearch()
  const navigate = useNavigate()
  const { data: flag } = useSuspenseQuery(flagDetailQuery(flagId))

  const [environment] = useState<Environment>(initialEnv)
  const [userId, setUserId] = useState("")
  const [value, setValue] = useState(flag.valueType === "boolean" ? "true" : "")
  const [reason, setReason] = useState("")
  const [expiresAt, setExpiresAt] = useState("")

  const createMutation = useMutation({
    mutationFn: createUserOverrideFn,
    onSuccess: () => {
      toast.success(t`Override created`)
      void navigate({
        to: "/settings/feature-flags/$flagId",
        params: { flagId },
      })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t`Failed to create override`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Parse value based on flag type
    let parsedValue: boolean | string | number = value
    if (flag.valueType === "boolean") {
      parsedValue = value.toLowerCase() === "true"
    } else if (flag.valueType === "number") {
      parsedValue = parseFloat(value) || 0
    }

    createMutation.mutate({
      data: {
        flagId,
        environment,
        userId,
        value: parsedValue,
        reason: reason || undefined,
        expiresAt: expiresAt ? new Date(expiresAt) : null,
      },
    })
  }

  const isValid = userId.length >= 1 && value.length >= 0

  return (
    <div className="flex-1 space-y-6 overflow-y-auto pb-8">
      <div>
        <Link
          to="/settings/feature-flags/$flagId"
          params={{ flagId }}
          className="mb-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t`Back to ${flag.name}`}
        </Link>
        <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
          {t`User Override`}
        </div>
        <h1 className="mb-2 font-serif text-4xl font-bold text-neutral-900 dark:text-white">
          {t`Create User Override`}
        </h1>
        <p className="text-neutral-500 dark:text-neutral-300">
          {t`Override flag value for a specific user`}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t`Override Details`}</CardTitle>
            <CardDescription>
              {t`Configure the user-specific override for ${flag.key}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="userId">{t`User ID`}</Label>
              <Input
                id="userId"
                placeholder="user-id-123"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="font-mono"
              />
              <p className="text-sm text-neutral-500">{t`The unique identifier of the user`}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="value">{t`Override Value`}</Label>
              {flag.valueType === "boolean" ? (
                <div className="flex items-center gap-4 pt-2">
                  <Switch
                    checked={value.toLowerCase() === "true"}
                    onCheckedChange={(v) => setValue(String(v))}
                  />
                  <span className="text-sm text-neutral-500">
                    {value.toLowerCase() === "true" ? "true" : "false"}
                  </span>
                </div>
              ) : (
                <Input
                  id="value"
                  type={flag.valueType === "number" ? "number" : "text"}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              )}
              <p className="text-sm text-neutral-500">
                {t`Value to return for this user (${flag.valueType})`}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">{t`Reason (optional)`}</Label>
              <Textarea
                id="reason"
                placeholder="Explain why this override is needed..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiresAt">{t`Expires At (optional)`}</Label>
              <Input
                id="expiresAt"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <p className="text-sm text-neutral-500">{t`Leave empty for permanent override`}</p>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  void navigate({
                    to: "/settings/feature-flags/$flagId",
                    params: { flagId },
                  })
                }
              >
                {t`Cancel`}
              </Button>
              <Button type="submit" disabled={!isValid || createMutation.isPending}>
                <Save className="mr-2 size-4" />
                {createMutation.isPending ? t`Creating...` : t`Create Override`}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
